import i18n from "i18next";
import {
    DescriptionTittle,
    IconAccessMethod,
    DoneTitle,
    IconCardFillAlert,
    IconCardFillMonitor,
    IconCardFillZone,
    IconCardNumber,
    IconController,
    IconCreateLayout,
    IconCriticalAlert,
    IconDocumentError,
    IconDocumentFile,
    IconDowntime,
    IconExpandPanel,
    IconKeyTitle,
    IconLastSeen,
    IconLoading,
    IconMobileChip,
    IconReaderSignal,
    IconResult,
    IconSessionStatus,
    IconSavedLayouts,
    IconTrigger,
    MarkerTitle,
    NotificationIcon,
    PersonChip,
    PersonChipBlack,
    PersonOutlinedChip,
    SettingsIcon,
    TrashOutlineIcon
} from "../../../../components/buttons/IconButtons";
import { colors } from "../../../../enums/colors";
import { status, STATUS_MODEL } from "../../../../enums/statusEnum";
import { getModelKeyById } from "../../../../Apis/Models";
import { errorCodeTranslation } from "../../../../Apis/ErrorCode";
import walkthroughGetStarted from "../../../../assets/ImagesDefaults/walkthrough-get-started.png";
import walkthroughOpenLayouts from "../../../../assets/ImagesDefaults/walkthrough-open-layouts.png";
import walkthroughAddPanel from "../../../../assets/ImagesDefaults/walkthrough-add-panel.png";
import walkthroughResizePanel from "../../../../assets/ImagesDefaults/walkthrough-resize-panel.png";
import walkthroughPanelActions from "../../../../assets/ImagesDefaults/walkthrough-panel-actions.png";
import { userTypeDrop } from "../../../../enums/userTypeEnumDrop";
import { userType, USER_TYPE_MODEL } from "../../../../enums/userTypeEnum";
import { TABLES } from "../../../../Apis/Tables";
import { EMPTY_VALUES, formatTimeUnit, userListToLabelEmailPhoneValue } from "../../../../Apis/functions";
import { accessMethod } from "../../../../enums/accessMethodEnum";

/**
 * @Author      Karen Yunuen Pozos Arias
 * @JobTitle    Full Stack
 * @Email       kpozos@ibl.mx
 */

export const LAYOUT_NAME_LENGTH = 128;
// Events of every page a panel reads while it is scrolled //
export const PANEL_PAGE_SIZE = 20;
// Distance to the bottom of a panel, in pixels, that reads its next page //
export const PANEL_SCROLL_THRESHOLD = 80;
// The canvas is a grid of 6x6 units: a half is 3 units, a third is 2 and the smallest panel is 2x2, 1/9 of the canvas //
export const GRID_UNITS = 6;
export const MIN_PANEL_SPAN = 2;
export const RESIZING_BODY_CLASS = 'event-monitoring-resizing';
// Separator of the names of the zones in the path of a zone panel //
export const ZONE_PATH_SEPARATOR = ' · ';

/**
 * Severity of an event, drives the chip, the side bar and the pictogram of every card.
 */
export const EVENT_SEVERITY = {
    CRITICAL: {
        ID: 'CRITICAL',
        BORDER_CLASS: 'border-full-error-code-400',
        ICON_CLASS: 'icon-color-error-code-700',
        BACKGROUND_CLASS: 'background-error-code-100',
        BACKGROUND: colors.errorCode100,
        FONT: colors.errorCode700,
        BORDER: colors.errorCode150,
    },
    WARNING: {
        ID: 'WARNING',
        BORDER_CLASS: 'border-full-warning-400',
        ICON_CLASS: 'icon-color-warning-700',
        BACKGROUND_CLASS: 'background-warning-100',
        BACKGROUND: colors.warning100,
        FONT: colors.warning700,
        BORDER: colors.warning150,
    },
    SUCCESS: {
        ID: 'SUCCESS',
        BORDER_CLASS: 'border-full-success-400',
        ICON_CLASS: 'icon-color-success-700',
        BACKGROUND_CLASS: 'background-success-100',
        BACKGROUND: colors.success100,
        FONT: colors.success700,
        BORDER: colors.success150,
    },
    INFO: {
        ID: 'INFO',
        BORDER_CLASS: 'border-full-secondary-400',
        ICON_CLASS: 'icon-color-secondary-700',
        BACKGROUND_CLASS: 'background-secondary-100',
        BACKGROUND: colors.secondary100,
        FONT: colors.secondary700,
        BORDER: colors.secondary150,
    },
    NEUTRAL: {
        ID: 'NEUTRAL',
        BORDER_CLASS: 'border-full-shades-300',
        ICON_CLASS: 'icon-color-shades-700',
        BACKGROUND_CLASS: 'background-shades-100',
        BACKGROUND: colors.shades100,
        FONT: colors.shades700,
        BORDER: colors.shades200,
    },
};

// Severities of the controller logs the zone overview shows as alerts, the same ones of the backend //
export const ZONE_OVERVIEW_ALERT_SEVERITIES = [EVENT_SEVERITY.CRITICAL.ID, EVENT_SEVERITY.WARNING.ID];

/**
 * How a panel draws its events.
 * FEED: one row per event. GROUPED: events grouped by area. HIGHLIGHT: large status cards.
 */
export const PANEL_VIEW = {
    FEED: 'FEED',
    GROUPED: 'GROUPED',
    HIGHLIGHT: 'HIGHLIGHT',
    UNCONFIGURED: 'UNCONFIGURED',
};

/**
 * Data source of a panel, chosen in the Add / Configure Panel modal.
 */
export const PANEL_TYPE = {
    ZONE: {
        ID: 'ZONE',
        TRANS: 'zone_panel',
        SECTION: 'select_zone',
        FIELD: 'zone',
        VIEW: PANEL_VIEW.FEED,
        TITLE: 'zone',
        Icon: MarkerTitle,
        CARD_ICON: IconCardFillZone,
        EMPTY_TRANS: 'there_are_no_events_in_this_zone_yet',
        // Without a zone the panel is the zone overview: one group per zone of the admin //
        SOURCE_OPTIONAL: true,
        OVERVIEW_TITLE: 'zone_overview',
        // The overview only reads the alerts of today //
        OVERVIEW_EMPTY_TRANS: 'there_are_no_events_in_your_zones_today',
        // The zones of the admin and all their children, the options and the chip show their name //
        SEARCH: { MODEL: TABLES.EVENT_MONITORING_ZONE, KEY_ID: 'id', LABEL: 'name' },
    },
    USER: {
        ID: 'USER',
        TRANS: 'user_panel',
        SECTION: 'select_a_user',
        FIELD: 'user',
        VIEW: PANEL_VIEW.FEED,
        TITLE: 'user',
        Icon: PersonChip,
        CARD_ICON: PersonChipBlack,
        EMPTY_TRANS: 'this_user_has_no_events_yet',
        SEARCH: { MODEL: TABLES.USER_BY_USER_TYPE, KEY_ID: 'userId', SHOW_SUB_SECTION: true, SANITIZE: userListToLabelEmailPhoneValue },
    },
    DEVICE: {
        ID: 'DEVICE',
        TRANS: 'device_panel',
        SECTION: 'select_device',
        FIELD: 'device',
        VIEW: PANEL_VIEW.HIGHLIGHT,
        TITLE: 'device',
        Icon: IconController,
        CARD_ICON: IconCardFillMonitor,
        EMPTY_TRANS: 'this_device_has_no_events_yet',
        SEARCH: { MODEL: TABLES.HID, KEY_ID: 'id', LABEL: 'name' },
    },
    EVENT: {
        ID: 'EVENT_TYPE',
        TRANS: 'event_type_panel',
        SECTION: 'select_event',
        FIELD: 'event',
        VIEW: PANEL_VIEW.FEED,
        TITLE: null,
        Icon: IconCriticalAlert,
        CARD_ICON: IconCardFillAlert,
        HEADER_SEVERITY: EVENT_SEVERITY.CRITICAL,
    },
};

export const UNCONFIGURED_PANEL = {
    TRANS: 'unconfigured_panel',
    Icon: IconLoading,
};

/**
 * Pictogram of the header of a panel without a severity of its own.
 */
export const HEADER_PICTOGRAM_DEFAULT = { BACKGROUND_CLASS: 'background-shades-100', ICON_CLASS: 'icon-color-general-950' };

export const PANEL_TYPE_OPTIONS = Object.values(PANEL_TYPE).map((type) => ({ id: type.ID, name: type.TRANS }));

export const getPanelType = (typeId) => Object.values(PANEL_TYPE).find((type) => type.ID === typeId) ?? null;

/**
 * Cell of the canvas each panel takes, following the auto placement of the CSS grid: every panel is
 * placed in the first free cell after the previous one. The backend stores these cells, so sorting the
 * panels by row and column gives back the order of the canvas.
 */
export const getPanelsGeometry = (panels = [], columns = 1) => {
    const occupied = new Set();
    const isFree = (row, col, width, height) => {
        if (col + width > columns) return false;
        for (let r = row; r < row + height; r++) {
            for (let c = col; c < col + width; c++) {
                if (occupied.has(`${r}-${c}`)) return false;
            }
        }
        return true;
    };

    let cursorRow = 0;
    let cursorCol = 0;
    return panels.map((panel) => {
        const width = Math.min(panel.colSpan, columns);
        const height = panel.rowSpan;
        let row = cursorRow;
        let col = cursorCol;
        while (!isFree(row, col, width, height)) {
            col += 1;
            if (col + width > columns) {
                col = 0;
                row += 1;
            }
        }
        for (let r = row; r < row + height; r++) {
            for (let c = col; c < col + width; c++) occupied.add(`${r}-${c}`);
        }
        cursorRow = row;
        cursorCol = col + width;
        return { id: panel.id, positionX: col, positionY: row, width: panel.colSpan, height };
    });
};

/**
 * Panel of the canvas. A panel without type is unconfigured until the admin selects its data source.
 * The backend sends its source as one id and one name, and its size as width (columns) and height (rows)
 * in units of the canvas.
 */
export const PANEL_MODEL = {
    id: null,
    typeId: null,
    sourceId: null,
    sourceName: null,
    userTypeId: null,
    colSpan: MIN_PANEL_SPAN,
    rowSpan: MIN_PANEL_SPAN,
    fromApi: (panel) => ({
        id: panel.id,
        typeId: panel.panelType ?? null,
        sourceId: panel.sourceId ?? null,
        sourceName: panel.sourceName ?? null,
        // The backend sends the names of the zones from the root to the zone of a zone panel, its title shows them as "Building A · Floor 3" //
        sourcePath: panel.sourcePath?.length ? panel.sourcePath.join(ZONE_PATH_SEPARATOR) : null,
        userTypeId: panel.userTypeId != null ? String(panel.userTypeId) : null,
        // A panel is never smaller than 1/9 of the canvas, even if it was stored with a smaller size //
        colSpan: Math.min(Math.max(panel.width ?? MIN_PANEL_SPAN, MIN_PANEL_SPAN), GRID_UNITS),
        rowSpan: Math.max(panel.height ?? MIN_PANEL_SPAN, MIN_PANEL_SPAN),
    }),
    toCreate: (layoutId, form, geometry) => ({
        layoutId,
        panelType: form.typeId,
        sourceId: form.sourceId,
        positionX: geometry.positionX,
        positionY: geometry.positionY,
        width: geometry.width,
        height: geometry.height,
    }),
    toUpdate: (panelId, form) => ({
        id: panelId,
        panelType: form.typeId,
        sourceId: form.sourceId,
    }),
};

/**
 * Events read for a panel. sourceKey is the source they belong to, so a panel whose source
 * changes starts again from its first page. loaded turns true once its first page arrives.
 */
export const PANEL_FEED_MODEL = {
    sourceKey: null,
    events: [],
    hasMore: true,
    loading: false,
    loaded: false,
    // Areas of the zone panels with the counters of all their events //
    areas: null,
    // Events read for every expanded area of the zone panels, by the id of the area //
    areaFeeds: {},
};

/**
 * Events read for an area of a zone panel. The area reads its own pages once it is expanded.
 */
export const AREA_FEED_MODEL = {
    events: [],
    hasMore: true,
    loading: false,
    loaded: false,
};

/**
 * Predefined structures shown in the Create Layout tab. Every slot becomes an unconfigured panel, so a
 * new layout starts with the structure of its thumbnail and can be changed afterwards. COLUMNS, ROWS and the spans of the
 * slots are cells of the structure, getCellUnits converts them into units of the canvas.
 */
export const LAYOUT_TYPE = {
    CUSTOM: {
        ID: 'CUSTOM',
        TRANS: 'custom',
        COLUMNS: 2,
        ROWS: 2,
        SLOTS: [{ colSpan: 1, rowSpan: 2 }, { colSpan: 1, rowSpan: 1 }],
        // The free cell of the thumbnail is the one the next added panel takes //
        PREVIEW: [{ colSpan: 1, rowSpan: 2 }, { colSpan: 1, rowSpan: 1 }, { colSpan: 1, rowSpan: 1, add: true }],
    },
    DEFAULT: {
        ID: 'DEFAULT',
        TRANS: 'default',
        COLUMNS: 2,
        ROWS: 2,
        SLOTS: [{ colSpan: 1, rowSpan: 2 }, { colSpan: 1, rowSpan: 1 }, { colSpan: 1, rowSpan: 1 }],
    },
    GRID: {
        ID: 'GRID',
        TRANS: 'grid',
        COLUMNS: 3,
        ROWS: 3,
        SLOTS: Array.from({ length: 9 }, () => ({ colSpan: 1, rowSpan: 1 })),
    },
    SPLIT: {
        ID: 'SPLIT',
        TRANS: 'split',
        COLUMNS: 2,
        ROWS: 2,
        SLOTS: Array.from({ length: 4 }, () => ({ colSpan: 1, rowSpan: 1 })),
    },
    SIDEBAR: {
        ID: 'SIDEBAR',
        TRANS: 'sidebar',
        COLUMNS: 3,
        ROWS: 2,
        SLOTS: [{ colSpan: 1, rowSpan: 2 }, { colSpan: 1, rowSpan: 1 }, { colSpan: 1, rowSpan: 1 }, { colSpan: 2, rowSpan: 1 }],
    },
    FOCUS: {
        ID: 'FOCUS',
        TRANS: 'focus',
        COLUMNS: 3,
        ROWS: 2,
        SLOTS: [{ colSpan: 3, rowSpan: 1 }, { colSpan: 1, rowSpan: 1 }, { colSpan: 1, rowSpan: 1 }, { colSpan: 1, rowSpan: 1 }],
    },
};

export const getLayoutType = (typeId) => Object.values(LAYOUT_TYPE).find((type) => type.ID === typeId) ?? LAYOUT_TYPE.CUSTOM;

/**
 * Units of the canvas a span of cells of the structure takes, so one cell of a 2x2 structure is 3x3 units
 * and one of a 3x3 structure is 2x2.
 */
export const getCellUnits = (type, colSpan = 1, rowSpan = 1) => ({
    colSpan: colSpan * (GRID_UNITS / type.COLUMNS),
    rowSpan: rowSpan * (GRID_UNITS / type.ROWS),
});

/**
 * Spans of a resized panel: the units the pointer covers, rounded and limited from the smallest panel
 * (1/9 of the canvas) to the whole canvas. The space a panel leaves when it shrinks stays empty.
 *
 * @param {number} colSpan - Columns the pointer covers, it can have decimals.
 * @param {number} rowSpan - Rows the pointer covers, it can have decimals.
 */
export const getPanelShape = (colSpan, rowSpan, columns, rows) => ({
    colSpan: Math.min(Math.max(Math.round(colSpan), MIN_PANEL_SPAN), columns),
    rowSpan: Math.min(Math.max(Math.round(rowSpan), MIN_PANEL_SPAN), rows),
});

/**
 * Sides a resized panel can no longer move to, with the cursor class set on the body while the pointer is past them.
 * COLUMNS: its width is at its smallest or largest size. ROWS: its height. BOTH: neither of them can change.
 */
export const PANEL_RESIZE_LIMIT = {
    COLUMNS: { ID: 'COLUMNS', BODY_CLASS: 'event-monitoring-resizing-limit-columns' },
    ROWS: { ID: 'ROWS', BODY_CLASS: 'event-monitoring-resizing-limit-rows' },
    BOTH: { ID: 'BOTH', BODY_CLASS: 'event-monitoring-resizing-limit-both' },
};

/**
 * Limit the pointer of a resize is past, null while the panel follows it on both sides. The spans are compared
 * without rounding, so the limit shows as soon as the pointer leaves the smallest or largest size of the panel.
 *
 * @param {number} colSpan - Columns the pointer covers, it can have decimals.
 * @param {number} rowSpan - Rows the pointer covers, it can have decimals.
 */
export const getPanelResizeLimit = (colSpan, rowSpan, columns, rows) => {
    const isOut = (span, max) => span < MIN_PANEL_SPAN || span > max;
    const columnsLimit = isOut(colSpan, columns);
    const rowsLimit = isOut(rowSpan, rows);
    if (columnsLimit && rowsLimit) return PANEL_RESIZE_LIMIT.BOTH;
    if (columnsLimit) return PANEL_RESIZE_LIMIT.COLUMNS;
    if (rowsLimit) return PANEL_RESIZE_LIMIT.ROWS;
    return null;
};

/**
 * Layout saved by the admin, the backend sends its panels sorted in the order of the canvas.
 */
export const LAYOUT_MODEL = {
    fromApi: (layout) => ({
        id: layout.id,
        name: layout.name,
        typeId: layout.layoutType,
        columns: GRID_UNITS,
        panels: (layout.panels ?? []).map(PANEL_MODEL.fromApi),
        // The simple search of the saved layouts brings the number of panels without them //
        totalPanels: layout.totalPanels ?? (layout.panels ?? []).length,
    }),
    toGeometry: (layout) => ({
        layoutId: layout.id,
        panels: getPanelsGeometry(layout.panels, layout.columns),
    }),
};

/**
 * Form of the Create Layout tab. The slots of the layout type are sent as unconfigured panels.
 */
export const CREATE_LAYOUT_MODEL = {
    name: '',
    typeId: null,
    clear: () => ({ ...CREATE_LAYOUT_MODEL, name: '', typeId: null }),
    isValid: (item) => !!item?.name?.trim() && item.name.trim().length <= LAYOUT_NAME_LENGTH && !!item?.typeId,
    toCreate: (item) => {
        const type = getLayoutType(item.typeId);
        return {
            name: item.name.trim(),
            layoutType: type.ID,
            panels: getPanelsGeometry(type.SLOTS.map((slot) => getCellUnits(type, slot.colSpan, slot.rowSpan)), GRID_UNITS)
                .map(({ id, ...geometry }) => geometry),
        };
    },
};

/**
 * Inline rename of a saved layout.
 */
export const RENAME_LAYOUT_MODEL = {
    id: null,
    name: '',
    clear: () => ({ ...RENAME_LAYOUT_MODEL, id: null, name: '' }),
    isValidName: (name) => !!name?.trim() && name.trim().length <= LAYOUT_NAME_LENGTH,
};

/**
 * Form shared by the Add Panel and Configure Panel modals.
 */
export const PANEL_FORM_MODEL = {
    typeId: null,
    sourceId: null,
    userTypeId: null,
    clear: () => ({ ...PANEL_FORM_MODEL, typeId: null, sourceId: null, userTypeId: null }),
    fromPanel: (panel) => ({ ...PANEL_FORM_MODEL, typeId: panel?.typeId ?? null, sourceId: panel?.sourceId ?? null, userTypeId: panel?.userTypeId ?? null }),
    // Chip of the source of a configured panel, the name of an event type is translated like its option //
    toSelectedSources: (panel) => {
        const type = getPanelType(panel?.typeId);
        if (!type || !panel?.sourceId) return [];
        const label = type.SEARCH ? panel.sourceName : i18n.t(panel.sourceName?.toLowerCase());
        return [{ id: panel.sourceId, label, Icon: type.CARD_ICON }];
    },
    isValidType: (item) => !!item?.typeId,
    // The user panels filter their users by a user type, the rest of the panels do not have one //
    isValidUserType: (item) => item?.typeId !== PANEL_TYPE.USER.ID || !!item?.userTypeId,
    isValidSource: (item) => !!item?.sourceId || !!getPanelType(item?.typeId)?.SOURCE_OPTIONAL,
};

/**
 * Options of the source. The event types come with the screen and are sent by their name, so their label
 * is translated. The zones, controllers and users are found by the simple search of their panel type.
 */
export const SOURCE_OPTION_MODEL = {
    toOption: (item, t) => ({ id: item.id, label: t(item.name?.toLowerCase()), Icon: PANEL_TYPE.EVENT.CARD_ICON }),
    /**
     * Sanitizer of the chip search: the id and the label are read from the fields of the search model.
     */
    toSearchOptions: (type) => type?.SEARCH?.SANITIZE ?? ((list, keyId, showSubSection, Icon) => (list ?? []).map((item) => ({
        id: item[keyId],
        label: item[type.SEARCH.LABEL],
        Icon,
    }))),
};

export const LAYOUTS_MODAL_TAB = {
    CREATE: {
        ID: 'create',
        TRANS: 'create_layout',
        ICON: IconCreateLayout,
    },
    SAVED: {
        ID: 'saved',
        TRANS: 'saved_layouts',
        ICON: IconSavedLayouts,
    },
};

export const DELETE_TARGET = {
    LAYOUT: {
        ID: 'LAYOUT',
        TITLE: 'delete_layout',
        ELEMENT: 'layout',
    },
    PANEL: {
        ID: 'PANEL',
        TITLE: 'delete_panel',
        ELEMENT: 'panel',
    },
};

/**
 * Modal the walkthrough opens while one of its steps is shown.
 */
export const WALKTHROUGH_MODAL = {
    LAYOUTS: 'LAYOUTS',
    ADD_PANEL: 'ADD_PANEL',
};

/**
 * Permission of the module a step or an action of the walkthrough explains.
 */
export const WALKTHROUGH_PERMISSION = {
    READ: 'READ',
    CREATE: 'CREATE',
    UPDATE: 'UPDATE',
    DELETE: 'DELETE',
};

// Time the modal opened by a step takes to finish its animation before the card looks for its target //
export const WALKTHROUGH_MODAL_DELAY = 400;

/**
 * Placement of the card of the walkthrough: 14px away from the element it points to, moved to another side when it
 * does not fit in the screen and kept 16px inside it. The arrow follows the element along the side of the card.
 */
export const WALKTHROUGH_POPPER_MODIFIERS = [
    { name: 'offset', options: { offset: [0, 14] } },
    { name: 'flip', options: { fallbackPlacements: ['left', 'top', 'bottom', 'right'], padding: 16 } },
    { name: 'preventOverflow', options: { padding: 16, altAxis: true, tether: false } },
    { name: 'arrow', options: { element: '[data-popper-arrow]', padding: 12 } },
];

/**
 * Welcome card of the walkthrough. It is centered, the module is shown without the sidebar, and it is not counted as a step.
 * TARGETS are CSS selectors; the first one found in the screen is the anchor, the card is centered when none is.
 */
export const WALKTHROUGH_INTRO = {
    TITLE: 'get_started',
    DESCRIPTION: 'walkthrough_get_started_description',
    BANNER: walkthroughGetStarted,
};

/**
 * Steps of the walkthrough, in the order the design shows them. PERMISSIONS lists the permissions that show a step
 * (any of them), and an action without PERMISSION is shown to everyone.
 */
export const WALKTHROUGH_STEPS = [
    {
        TITLE: 'open_layouts',
        DESCRIPTION: 'walkthrough_open_layouts_description',
        TARGETS: ['#btn_emm_layouts'],
        PLACEMENT: 'bottom-end',
        BANNER: walkthroughOpenLayouts,
        PERMISSIONS: [WALKTHROUGH_PERMISSION.CREATE],
    },
    {
        TITLE: 'create_a_layout',
        DESCRIPTION: 'walkthrough_create_layout_description',
        TARGETS: ['.modal.show #btn-cm-confirm'],
        PLACEMENT: 'right',
        MODAL: WALKTHROUGH_MODAL.LAYOUTS,
        PERMISSIONS: [WALKTHROUGH_PERMISSION.CREATE],
    },
    {
        TITLE: 'add_a_panel',
        DESCRIPTION: 'walkthrough_add_panel_description',
        // Without a layout the canvas only shows its "Create layout" action, the card points to it instead of covering it //
        TARGETS: ['#btn_cbl_action_btn_pes_add_a_panel', '#btn_cbl_action_btn_lb_add_panel', '#btn_cbl_action_btn_pes_create_layout'],
        PLACEMENT: 'right',
        BANNER: walkthroughAddPanel,
        PERMISSIONS: [WALKTHROUGH_PERMISSION.CREATE],
    },
    {
        TITLE: 'select_a_panel_type',
        DESCRIPTION: 'walkthrough_select_panel_type_description',
        TARGETS: ['.modal.show #btn-cm-confirm'],
        PLACEMENT: 'right',
        MODAL: WALKTHROUGH_MODAL.ADD_PANEL,
        PERMISSIONS: [WALKTHROUGH_PERMISSION.CREATE],
    },
    {
        TITLE: 'resize_the_panel',
        DESCRIPTION: 'walkthrough_resize_panel_description',
        TARGETS: ['.event-monitoring-resize-handle'],
        PLACEMENT: 'right-start',
        PANEL: true,
        BANNER: walkthroughResizePanel,
        PERMISSIONS: [WALKTHROUGH_PERMISSION.UPDATE],
    },
    {
        TITLE: 'panel_actions',
        DESCRIPTION: 'walkthrough_panel_actions_description',
        TARGETS: ['.event-monitoring-panel-actions'],
        PLACEMENT: 'right-start',
        PANEL: true,
        BANNER: walkthroughPanelActions,
        PERMISSIONS: [WALKTHROUGH_PERMISSION.READ, WALKTHROUGH_PERMISSION.UPDATE, WALKTHROUGH_PERMISSION.DELETE],
        ACTIONS: [
            { KEY: 'expand', TRANS: 'walkthrough_expand_action', Icon: IconExpandPanel, PERMISSION: WALKTHROUGH_PERMISSION.READ },
            { KEY: 'configure', TRANS: 'walkthrough_configure_action', Icon: SettingsIcon, PERMISSION: WALKTHROUGH_PERMISSION.UPDATE },
            { KEY: 'remove', TRANS: 'walkthrough_remove_action', Icon: TrashOutlineIcon, PERMISSION: WALKTHROUGH_PERMISSION.DELETE },
        ],
    },
];

/**
 * Steps of the walkthrough the admin can follow: only the ones of its permissions, and in the panel actions only
 * the buttons the panel shows to it.
 *
 * @param {Object} allowed - Whether the admin can create, update and delete, keyed by WALKTHROUGH_PERMISSION.
 */
export const getWalkthroughSteps = (allowed) => WALKTHROUGH_STEPS
    .filter((step) => step.PERMISSIONS.some((key) => allowed[key]))
    .map((step) => step.ACTIONS
        ? { ...step, ACTIONS: step.ACTIONS.filter((action) => !action.PERMISSION || allowed[action.PERMISSION]) }
        : step);

// Id of the example panel of the walkthrough, it is never sent to the backend //
export const WALKTHROUGH_MOCK_PANEL_ID = 'walkthrough_mock_panel';

/**
 * Layout shown by the steps of the walkthrough that point to a panel (PANEL) while the canvas has none: the active
 * layout, or an example one when there is no layout, with one unconfigured panel. It is only drawn on the canvas,
 * the active layout is not changed, so the example panel is gone once the step ends.
 *
 * @param {Object} layout - Active layout, null when the admin has not created one.
 * @param {Function} t - Translation function.
 */
export const getWalkthroughMockLayout = (layout, t) => ({
    id: layout?.id ?? WALKTHROUGH_MOCK_PANEL_ID,
    name: layout?.name ?? t('layout'),
    typeId: layout?.typeId ?? LAYOUT_TYPE.CUSTOM.ID,
    columns: GRID_UNITS,
    panels: [{
        id: WALKTHROUGH_MOCK_PANEL_ID,
        typeId: null,
        sourceId: null,
        sourceName: null,
        userTypeId: null,
        ...getCellUnits(getLayoutType(layout?.typeId)),
    }],
    totalPanels: 1,
});

export const EVENT_DEFAULT_ICON = NotificationIcon;

/**
 * Event types, the sources of the event type panels. The critical alerts panels show the controller logs of
 * every HID reason; the reports are the ones of the user shown in the notifications.
 */
export const EVENT_TYPE = {
    CRITICAL_ALERTS: 'CRITICAL_ALERTS',
    REPORTS: 'REPORTS',
    // Controller logs that are not critical alerts //
    DEVICE: 'DEVICE',
};

/**
 * Event types an event type panel can show; DEVICE is not a source of its own.
 */
export const EVENT_TYPE_SOURCES = [EVENT_TYPE.CRITICAL_ALERTS, EVENT_TYPE.REPORTS]
    .map((eventType) => ({ id: eventType, name: eventType }));

/**
 * Event types of the events every event type panel receives from the socket, by its source. The critical alerts
 * panels take the controller logs of every HID reason (message_type_code), the device ones included.
 */
export const EVENT_TYPE_PANEL_EVENT_TYPES = {
    [EVENT_TYPE.CRITICAL_ALERTS]: [EVENT_TYPE.CRITICAL_ALERTS, EVENT_TYPE.DEVICE],
    [EVENT_TYPE.REPORTS]: [EVENT_TYPE.REPORTS],
};

/**
 * Severities an event type panel does not show, by its source. The critical alerts panels leave out the controller
 * logs that went well (SUCCESS) and the informative ones (INFO); the zone, user and device panels still show them.
 */
export const EVENT_TYPE_PANEL_EXCLUDED_SEVERITIES = {
    [EVENT_TYPE.CRITICAL_ALERTS]: [EVENT_SEVERITY.SUCCESS.ID, EVENT_SEVERITY.INFO.ID],
};

/**
 * User types the user panel filters its users by. The id is a string, like the user type of a panel read from the backend.
 */
export const PANEL_USER_TYPES = Object.values(USER_TYPE_MODEL)
    .filter((id) => id !== USER_TYPE_MODEL.UNKNOWN)
    .map((id) => ({ id: String(id), name: userType[id] }));

/**
 * Text of the empty state of an event type panel, by its event type.
 */
export const EVENT_TYPE_EMPTY_TRANS = {
    [EVENT_TYPE.CRITICAL_ALERTS]: 'there_are_no_critical_alerts_yet',
    [EVENT_TYPE.REPORTS]: 'you_have_not_generated_any_reports_yet',
};

/**
 * Text of the empty state of an area of the zone panel.
 */
export const AREA_EMPTY_TRANS = 'there_are_no_events_in_this_area_today';

/**
 * Area of a zone panel with the counters of all its events. In the zone overview the area is a zone of the admin
 * with the counters of today of its critical alerts, the critical and the warning ones apart.
 */
export const AREA_MODEL = {
    fromApi: (area) => ({
        id: area.id,
        name: area.name,
        total: area.total ?? 0,
        critical: area.critical ?? 0,
        warning: area.warning ?? 0,
        lastAt: area.lastAt ?? 0,
    }),
};

/**
 * Rows a card of a controller log can show, built from the data of the event. Every row of its HID reason is shown:
 * a row whose data did not arrive shows EMPTY_VALUES.DEFAULT, so the missing data can be seen.
 */
export const CONTROLLER_LOG_ROW = {
    CONTROLLER: (event) => ({ Icon: IconController, label: 'controller', value: event.hidName || EMPTY_VALUES.DEFAULT }),
    // The controller does not send the door of its logs yet //
    DOOR: (event) => ({ Icon: IconDocumentError, label: 'door', value: event.doorName || EMPTY_VALUES.DEFAULT }),
    ZONE: (event) => ({ Icon: MarkerTitle, label: 'zone', value: event.zoneName || EMPTY_VALUES.DEFAULT }),
    // Text the controller sent with the log, as what triggered it or as the result of the action //
    TRIGGER: (event) => ({ Icon: IconTrigger, label: 'trigger', value: event.description || EMPTY_VALUES.DEFAULT }),
    RESULT: (event) => ({ Icon: IconResult, label: 'result', value: event.description || EMPTY_VALUES.DEFAULT }),
    CARD: (event) => ({
        Icon: IconCardNumber,
        label: 'card',
        value: event.cardNumber ? `FC: ${event.facilityCode ?? EMPTY_VALUES.DEFAULT} • CN: ${event.cardNumber}` : EMPTY_VALUES.DEFAULT,
    }),
    // Time between the disconnection of the controller and its reconnection //
    DOWNTIME: (event) => ({ Icon: IconDowntime, label: 'downtime', value: event.downSince ? getDuration(event.downSince, event.occurredAt) : EMPTY_VALUES.DEFAULT }),
    RESTORED: (event) => ({
        Icon: DoneTitle,
        label: 'restored',
        value: event.doors != null ? i18n.t('X_doors_normal_mode', { count: event.doors }) : EMPTY_VALUES.DEFAULT,
    }),
    AFFECTED: (event) => ({
        Icon: IconReaderSignal,
        label: 'affected',
        value: event.doors != null ? i18n.t('X_doors_no_response', { count: event.doors }) : EMPTY_VALUES.DEFAULT,
    }),
    // Time since the last log of the controller before it disconnected, it grows with now //
    LAST_SEEN: (event) => (event.lastSeenAt
        ? { Icon: IconLastSeen, label: 'last_seen', elapsedFrom: event.lastSeenAt }
        : { Icon: IconLastSeen, label: 'last_seen', value: EMPTY_VALUES.DEFAULT }),
};

/**
 * Description of a card of a controller log, built with the phrase of its HID reason (<reason>_description).
 * CONTROLLER: "CTL-005 back online · Executive Floor". DOOR: "Relay triggered successfully · Door-A2".
 */
export const CONTROLLER_LOG_SUBTITLE = {
    CONTROLLER: (event, phrase) => `${[event.hidName || EMPTY_VALUES.DEFAULT, phrase].filter(Boolean).join(' ')} · ${event.zoneName || EMPTY_VALUES.DEFAULT}`,
    DOOR: (event, phrase) => `${phrase || EMPTY_VALUES.DEFAULT} · ${event.doorName || EMPTY_VALUES.DEFAULT}`,
};

const CONTROLLER_ONLINE = {
    TITLE: 'controller_reconnected',
    HIGHLIGHT_PICTOGRAM: IconController,
    SUBTITLE: CONTROLLER_LOG_SUBTITLE.CONTROLLER,
    ROWS: [CONTROLLER_LOG_ROW.CONTROLLER, CONTROLLER_LOG_ROW.DOWNTIME, CONTROLLER_LOG_ROW.RESTORED],
};
const CONTROLLER_OFFLINE = {
    TITLE: 'controller_offline',
    HIGHLIGHT_PICTOGRAM: IconController,
    SUBTITLE: CONTROLLER_LOG_SUBTITLE.CONTROLLER,
    ROWS: [CONTROLLER_LOG_ROW.CONTROLLER, CONTROLLER_LOG_ROW.AFFECTED, CONTROLLER_LOG_ROW.LAST_SEEN],
};
const CONTROLLER_ALERT = {
    SUBTITLE: CONTROLLER_LOG_SUBTITLE.CONTROLLER,
    ROWS: [CONTROLLER_LOG_ROW.CONTROLLER, CONTROLLER_LOG_ROW.ZONE, CONTROLLER_LOG_ROW.TRIGGER],
};
const CONTROLLER_REPLY = {
    SUBTITLE: CONTROLLER_LOG_SUBTITLE.CONTROLLER,
    ROWS: [CONTROLLER_LOG_ROW.CONTROLLER, CONTROLLER_LOG_ROW.TRIGGER],
};

/**
 * Card of every HID reason (HidTypeMessageReason of the backend, read from the message_type_code of the log): its title
 * when it is not the name of the reason, its description and its rows. The cards of the device panels show a bell, the
 * reconnections and disconnections the controller (HIGHLIGHT_PICTOGRAM). A reason not registered is shown as a reply.
 */
export const CONTROLLER_LOG_STRUCTURE = {
    UKNOWN: CONTROLLER_REPLY,
    DEVICE_CHANNEL_DISCONNECTED: CONTROLLER_OFFLINE,
    DEVICE_COMMUNICATION_NOT_YET_ATTEMPTED: {
        SUBTITLE: CONTROLLER_LOG_SUBTITLE.CONTROLLER,
        ROWS: [CONTROLLER_LOG_ROW.CONTROLLER, CONTROLLER_LOG_ROW.ZONE],
    },
    DEVICE_OFFLINE: CONTROLLER_OFFLINE,
    DEVICE_ONLINE: CONTROLLER_ONLINE,
    HOST_ONLINE: CONTROLLER_ONLINE,
    HOST_OFFLINE: CONTROLLER_OFFLINE,
    CONFIGURATION_DB_SAVE_COMPLETE: { SUBTITLE: CONTROLLER_LOG_SUBTITLE.CONTROLLER, ROWS: [CONTROLLER_LOG_ROW.CONTROLLER] },
    CARD_DB_SAVE_COMPLETE: { SUBTITLE: CONTROLLER_LOG_SUBTITLE.CONTROLLER, ROWS: [CONTROLLER_LOG_ROW.CONTROLLER] },
    READER_DISCONNECTED: CONTROLLER_ALERT,
    READER_UNKOWN: CONTROLLER_ALERT,
    READER_FAIL: CONTROLLER_ALERT,
    READER_SECURE: CONTROLLER_ALERT,
    DOOR_SENSOR_NOT_CONFIGURED: CONTROLLER_ALERT,
    DOOR_SENSOR_UNKNOWN_RS_BITS: {
        TITLE: 'door_held_open',
        SUBTITLE: CONTROLLER_LOG_SUBTITLE.DOOR,
        ROWS: [CONTROLLER_LOG_ROW.DOOR, CONTROLLER_LOG_ROW.ZONE, CONTROLLER_LOG_ROW.CONTROLLER],
    },
    DOOR_SENSOR_ALARM: CONTROLLER_ALERT,
    DOOR_SENSOR_SECURE: {
        TITLE: 'relay_activated',
        SUBTITLE: CONTROLLER_LOG_SUBTITLE.DOOR,
        ROWS: [CONTROLLER_LOG_ROW.CONTROLLER, CONTROLLER_LOG_ROW.DOOR, CONTROLLER_LOG_ROW.ZONE, CONTROLLER_LOG_ROW.RESULT],
    },
    DOOR_SENSOR_DISCONNECTED: CONTROLLER_ALERT,
    DOOR_SENSOR_FAULT: CONTROLLER_ALERT,
    NAK_INVALID_PACKET_HEADER: CONTROLLER_REPLY,
    NAK_CONTENT_ERROR: CONTROLLER_REPLY,
    NAK_CANNOT_EXECUTE: CONTROLLER_REPLY,
    NAK_PORT_IS_STANDBY: CONTROLLER_REPLY,
    NAK_FAILED_LOGON: CONTROLLER_REPLY,
    NAK_COMMAND_NOT_ACCEPTED_DEGRADED_MODE: CONTROLLER_REPLY,
    NAK_INVALID_COMMAND_TYPE: CONTROLLER_REPLY,
    CARD_IS_NOT_IN_FILE: {
        SUBTITLE: CONTROLLER_LOG_SUBTITLE.CONTROLLER,
        ROWS: [CONTROLLER_LOG_ROW.CARD, CONTROLLER_LOG_ROW.ZONE, CONTROLLER_LOG_ROW.CONTROLLER],
    },
};

export const getControllerLogStructure = (reason) => CONTROLLER_LOG_STRUCTURE[reason] ?? CONTROLLER_REPLY;

/**
 * Statuses of a report that failed. A ready report whose path is an error code also failed.
 */
const REPORT_ERROR_STATUSES = [STATUS_MODEL.REPORT_SERVER_ERROR, STATUS_MODEL.REPORT_GENERATING_ERROR];

/**
 * Statuses of a report that has a file.
 */
const REPORT_FILE_STATUSES = [STATUS_MODEL.REPORT_READY, STATUS_MODEL.REPORT_EXPIRED];

/**
 * Reasons of the log ins (log_in_log) sent by the backend, the rest of its events are controller logs and accesses.
 */
export const LOG_IN_REASONS = ['LOG_IN_SUCCESSFUL', 'LOG_IN_FAILED'];

/**
 * Translation key of the platform of a log in (log_in_log.platform).
 */
export const LOG_IN_PLATFORM = { WEB: 'web_browser', MOBILE: 'mobile_app' };

/**
 * Event shaped for the cards. The label is a translation key: the name of the HID reason of a controller
 * log, access_log_granted / access_log_denied for an access, login / failed_login for a log in, or
 * report_event / error_report for a report.
 */
export const EVENT_MODEL = {
    /**
     * Event received from the backend, which categorizes the controller logs by their HID reason: it sends
     * their event type and severity. The access events show the user as description; the controller logs
     * are shaped by fromControllerLog.
     */
    fromApi: (event) => {
        if (event.reasonCode !== undefined) return EVENT_MODEL.fromControllerLog(event);
        if (LOG_IN_REASONS.includes(event.reason)) return EVENT_MODEL.fromLogIn(event);

        const reason = event.reason?.toLowerCase();
        return {
            id: event.id,
            date: event.occurredAt,
            // Date the next page of the panel is read from //
            cursor: event.occurredAt,
            // A granted access is green (SUCCESS) and a denied one red (CRITICAL), like the backend sends them //
            severity: event.success === false ? EVENT_SEVERITY.CRITICAL.ID : EVENT_SEVERITY.SUCCESS.ID,
            eventType: event.eventType ?? null,
            // Chip "Access log granted", description "Access granted to <user>" //
            label: `access_log_${event.success === false ? 'denied' : 'granted'}`,
            // Squared chip like the rest of the events; the pill one is only for the logins //
            chipVariant: 'large',
            title: reason,
            description: i18n.t(`${reason}_to_name`, { name: event.userName ?? EMPTY_VALUES.DEFAULT, interpolation: { escapeValue: false } }),
            pictogram: IconKeyTitle,
            userId: event.userId ?? null,
            deviceId: event.hidId ?? null,
            // The zone of the access and its parents, the live event reaches the panels of the parent zones too //
            zoneIds: event.zoneIds ?? (event.zoneId ? [event.zoneId] : []),
            // Access method, device (the reader of the door or the device) and zone, followed by the date //
            meta: [
                { Icon: IconAccessMethod, label: 'access_method', value: accessMethod[event.accessMethodId] ? i18n.t(accessMethod[event.accessMethodId]) : EMPTY_VALUES.DEFAULT },
                { Icon: IconMobileChip, label: 'device', value: event.areaName || EMPTY_VALUES.DEFAULT },
                { Icon: MarkerTitle, label: 'zone', value: event.zoneName || EMPTY_VALUES.DEFAULT },
            ],
        };
    },

    /**
     * Log in of a user (log_in_log), shown by the user panels: "Login" chip and "Access granted to <user>", followed by
     * the user type and name, the platform and the result.
     */
    fromLogIn: (event) => {
        const successful = event.success !== false;
        const name = event.userName ?? EMPTY_VALUES.DEFAULT;
        const userTypeName = event.userTypeId && userTypeDrop[event.userTypeId] ? i18n.t(userTypeDrop[event.userTypeId]) : EMPTY_VALUES.DEFAULT;
        return {
            id: event.id,
            date: event.occurredAt,
            // Date the next page of the panel is read from //
            cursor: event.occurredAt,
            // A successful log in is blue (INFO) and a failed one yellow (WARNING), like the backend sends them //
            severity: successful ? EVENT_SEVERITY.INFO.ID : EVENT_SEVERITY.WARNING.ID,
            eventType: null,
            label: successful ? 'login' : 'failed_login',
            title: successful ? 'login' : 'failed_login',
            // Same title as the accesses: "Access granted to <user>" //
            description: i18n.t(successful ? 'access_granted_to_name' : 'access_denied_to_name', { name, interpolation: { escapeValue: false } }),
            pictogram: IconKeyTitle,
            userId: event.userId ?? null,
            deviceId: null,
            meta: [
                { Icon: PersonOutlinedChip, label: 'user', value: `${userTypeName} • ${name}` },
                { Icon: IconMobileChip, label: 'platform', value: LOG_IN_PLATFORM[event.platform] ? i18n.t(LOG_IN_PLATFORM[event.platform]) : EMPTY_VALUES.DEFAULT },
                { Icon: IconSessionStatus, label: 'status', value: i18n.t(successful ? 'log_in_successful' : 'log_in_failed'), newRow: true },
            ],
        };
    },

    /**
     * Controller log. The description is the controller, what happened to it by its HID reason (<reason>_description)
     * and the zones of its doors: "CTL-005 back online · Executive Floor". Its title, description and rows are the
     * ones of its HID reason in CONTROLLER_LOG_STRUCTURE.
     */
    fromControllerLog: (event) => {
        const reason = event.reason?.toLowerCase();
        const structure = getControllerLogStructure(event.reason);
        const reasonDescription = `${reason}_description`;
        const phrase = i18n.exists(reasonDescription) ? i18n.t(reasonDescription) : null;
        return {
            id: event.id,
            date: event.occurredAt,
            // Date the next page of the panel is read from //
            cursor: event.occurredAt,
            severity: event.severity,
            eventType: event.eventType ?? null,
            label: structure.TITLE ?? reason,
            // The chip of the rows of the controller logs is large, like the one of the reports //
            chipVariant: 'large',
            title: structure.TITLE ?? reason,
            description: structure.SUBTITLE(event, phrase),
            // The rows of the critical alerts panels show a bell for the logs of every HID reason //
            pictogram: NotificationIcon,
            highlightPictogram: structure.HIGHLIGHT_PICTOGRAM ?? NotificationIcon,
            userId: null,
            deviceId: event.hidId ?? null,
            // The live logs come with the zones of the doors of their controller and their parents, the zone overview takes them by them //
            zoneIds: event.zoneIds ?? [],
            controllerLog: true,
            meta: structure.ROWS.map((row) => row(event)),
        };
    },

    /**
     * Report of the notifications or of a page of reports. The description is the name of the report; the file is
     * shown while the report has one, and the error while it failed. A failed report is critical, the rest are info.
     * The user who requested it is shown as "user type • name" in its own row, followed by the date.
     */
    fromReport: (report, requestedBy) => {
        const requestedByValue = [requestedBy?.userTypeId && i18n.t(userTypeDrop[requestedBy.userTypeId]), requestedBy?.name]
            .filter(Boolean).join(' • ');
        const reportName = i18n.t(getModelKeyById(report.moduleId));
        const errorCode = errorCodeTranslation[report.path];
        const failed = REPORT_ERROR_STATUSES.includes(report.statusId) || !!errorCode;
        const hasFile = !failed && REPORT_FILE_STATUSES.includes(report.statusId) && !!report.path;
        return {
            id: `report_${report.id}`,
            date: report.endAt ?? report.startAt,
            // The reports are read by their start, the order of the notifications //
            cursor: report.startAt,
            severity: failed ? EVENT_SEVERITY.CRITICAL.ID : EVENT_SEVERITY.INFO.ID,
            eventType: EVENT_TYPE.REPORTS,
            label: failed ? 'error_report' : 'report_event',
            // The chip of the reports and the critical alerts is large, the rest of the events keep the compact one //
            chipVariant: 'large',
            title: status[report.statusId],
            // The module alone does not say it is a report, so it is shown as "Report of <module>" //
            description: i18n.t('report_of_name', { name: reportName, interpolation: { escapeValue: false } }),
            pictogram: DescriptionTittle,
            userId: null,
            deviceId: null,
            meta: [
                hasFile && { Icon: IconDocumentFile, label: 'file', value: report.path },
                failed && {
                    Icon: IconDocumentError,
                    label: 'error',
                    value: i18n.t(errorCode ?? status[report.statusId], { name: reportName, interpolation: { escapeValue: false } }),
                    showLabel: true,
                },
                requestedByValue && { Icon: PersonChip, label: 'requested_by', value: requestedByValue, newRow: true },
            ].filter(Boolean),
        };
    },
};

/**
 * Time between two dates: "12 minutes", "1h 2m".
 */
export const getDuration = (from, to) => {
    const minutes = Math.max(0, Math.floor((new Date(to).getTime() - new Date(from).getTime()) / 60000));
    return minutes < 60 ? i18n.t('X_minutes', { count: minutes }) : getElapsedTime(from, new Date(to).getTime());
};

/**
 * Value of a meta item. The items with elapsedFrom show the time since that date, so they grow with now: "1h 2m ago".
 */
export const getMetaValue = (item, now, t) => (item.elapsedFrom ? t('X_MIN_ago', { min: getElapsedTime(item.elapsedFrom, now) }) : item.value);

/**
 * Start of the day of a date in the time zone of the admin, the zone overview counts the events since it.
 */
export const getStartOfDay = (date) => new Date(date).setHours(0, 0, 0, 0);

/**
 * Elapsed time of an event in a short form: "10m", "1h 2m", "2h".
 */
export const getElapsedTime = (date, now = Date.now()) =>
    formatTimeUnit(Math.max(0, Math.floor((now - new Date(date).getTime()) / 60000)));
